/**
 * O modo claro/escuro sem o Mantine: o mesmo que o `HongaProvider` faz (a classe `dark` no
 * `<html>`, a escolha guardada em `vite-ui-theme`, «system» a seguir o aparelho), para os
 * portais que já não carregam o Mantine. A chave é a mesma, por isso quem já tinha escolhido
 * o modo escuro continua com ele.
 */
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

export type Tema = 'light' | 'dark' | 'system';

interface EstadoTema {
    tema: Tema;
    setTema: (tema: Tema) => void;
    /** O que se vê agora (com «system», o do aparelho). */
    escuro: boolean;
}

const ContextoTema = createContext<EstadoTema>({ tema: 'system', setTema: () => undefined, escuro: false });

function sistemaEscuro(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function lerTema(chave: string, omissao: Tema): Tema {
    try {
        const guardado = localStorage.getItem(chave);
        return guardado === 'light' || guardado === 'dark' || guardado === 'system' ? guardado : omissao;
    } catch {
        return omissao;
    }
}

export function TemaProvider({ children, omissao = 'system', chave = 'vite-ui-theme' }: {
    children: ReactNode;
    omissao?: Tema;
    chave?: string;
}) {
    const [tema, setTemaInterno] = useState<Tema>(() => lerTema(chave, omissao));
    const [doSistema, setDoSistema] = useState(sistemaEscuro);

    // Com «system», segue o aparelho também quando ele muda.
    useEffect(() => {
        if (tema !== 'system' || typeof window === 'undefined') return;
        const consulta = window.matchMedia('(prefers-color-scheme: dark)');
        const mudar = () => setDoSistema(consulta.matches);
        mudar();
        consulta.addEventListener('change', mudar);
        return () => consulta.removeEventListener('change', mudar);
    }, [tema]);

    const escuro = tema === 'dark' || (tema === 'system' && doSistema);

    useEffect(() => {
        const raiz = document.documentElement;
        raiz.classList.remove('light', 'dark');
        raiz.classList.add(escuro ? 'dark' : 'light');
        raiz.style.colorScheme = escuro ? 'dark' : 'light';
    }, [escuro]);

    const setTema = (novo: Tema) => {
        try {
            localStorage.setItem(chave, novo);
        } catch {
            // Sem localStorage a escolha dura até fechar a página.
        }
        setTemaInterno(novo);
    };

    return <ContextoTema.Provider value={{ tema, setTema, escuro }}>{children}</ContextoTema.Provider>;
}

export function useTema(): EstadoTema {
    return useContext(ContextoTema);
}
